import React, { memo, useMemo, useCallback, useContext, useEffect } from 'react'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel'
import Spacing from 'components/Common/Spacing'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_ALWAYS_ON,
} from 'constants/offer'
import YouMayAlsoLikeTile from 'components/OfferList/OfferListTiles/YouMayAlsoLikeTile'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import LayoutContainer from 'components/Common/LayoutContainer'
import { productClick } from 'analytics/enhanced-ecommerce'
import { productClickObject } from 'analytics/eventDefinitions'
import TrackingContext from 'contexts/trackingContext'
import GeoContext from 'contexts/geoContext'
import { buildSearchStringFromFilters } from 'lib/searchUtils'
import useBatchBedbankStickerRates from './useBatchBedbankStickerRates'
import { removeBedbankPropertiesWithLEOffer } from './utils'

import Heading from 'components/Luxkit/Typography/Heading'
import uuidV4 from 'lib/uuidV4'
import { useOfferList } from 'hooks/Offers/useOfferList'
import { detectOfferTypeFromId } from 'lib/offer/offerUtils'
import { skip, take, without } from 'lib/arrayUtils'
import useOffers from 'hooks/Offers/useOffers'
import { EmptyObject } from 'lib/objectUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getGraphRecommendations } from 'selectors/recomendations/graphSelectors'
import { useInView } from 'react-intersection-observer'
import { isLpcHotelType } from 'lib/offer/offerTypes'
import { clickRecommendationOnLpcPage, viewRecommendationOnLpcPage } from 'analytics/optimizely/events'
import * as Analytics from 'analytics/analytics'

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  title?: string;
  offerId?: string;
  offerType?: App.OfferType;
  filters?: App.OfferListFilters;
  trackingDescription: string;
  useGraph?: boolean;
  skipFirstNOffers?: number;
}

const MINIMUM_OFFERS_TO_DISPLAY = 2
const DISPLAY_PAGE_SIZE = 4
const MAXIMUM_PAGES = 2

export const RECOMMENDATION_TYPES: App.OfferType[] = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_LAST_MINUTE,
  'bedbank_hotel',
]

function OfferRecommendations(props: Props) {
  const {
    title = 'You may also like',
    offerId,
    offerType,
    filters = emptyFilters,
    trackingDescription,
    useGraph = false,
    skipFirstNOffers,
  } = props

  const geo = useContext(GeoContext)
  const [offerList] = useOfferList({ ...filters, offerTypes: RECOMMENDATION_TYPES })

  const { offerIds: graphOfferIds, state: graphState } = useAppSelector<App.OfferBasedGraphRecommendation>(
    state => getGraphRecommendations(state, offerId),
  )

  const offerIds = useMemo(() => {
    let offerCandidates = useGraph ? graphOfferIds : offerList.offerIds

    // remove source offer
    offerCandidates = without(offerCandidates, offerId ?? '')

    // skip first few offers if specified
    if (skipFirstNOffers) {
      offerCandidates = skip(offerCandidates, skipFirstNOffers)
    }

    // take max offers
    offerCandidates = take(offerCandidates, DISPLAY_PAGE_SIZE * MAXIMUM_PAGES)

    return offerCandidates
  }, [offerId, offerList.offerIds, useGraph, graphOfferIds, skipFirstNOffers])

  const [offers, offersFetching] = useOffers(offerIds)

  const algorithm = useGraph ? 'Graph' : (filters.propertyId ? 'Search Nearby' : 'Algolia')
  const listName = ['Recommendations', algorithm, trackingDescription].filter(Boolean).join(' - ')

  const tracking = useMemo(() => ({
    category: 'Recommendations',
    label: [algorithm, trackingDescription].filter(Boolean).join(' - '),
    listName,
    listId: uuidV4(),
  }), [trackingDescription, algorithm, listName])

  const bedbankOfferIds = useMemo(() =>
    offerList.offerIds.filter(id => detectOfferTypeFromId(id) === 'bedbank_hotel')
  , [offerList.offerIds])

  const {
    hasLoading: bedbankRatesLoading,
  } = useBatchBedbankStickerRates(
    bedbankOfferIds,
    filters.rooms,
    filters.checkIn,
    filters.checkOut,
  )

  const isFetching = (
    (useGraph && graphState === 'loading') ||
    (!useGraph && offerList.fetching) ||
    offersFetching ||
    bedbankRatesLoading
  )

  const offersToDisplay = useMemo(() => {
    if (isFetching) {
      return []
    }

    return removeBedbankPropertiesWithLEOffer(offers)
  }, [isFetching, offers])

  const isSourceLpc = isLpcHotelType(offerType)

  const onClick = useCallback((
    offer: App.OfferSummary | App.BedbankOfferWithRate,
    position: number,
  ) => {
    productClick(geo.currentCurrency, productClickObject(offer, position), {
      listId: tracking.listId,
      listName,
    })
    if (isSourceLpc) {
      Analytics.track('click-recommendation-on-lpc-page', clickRecommendationOnLpcPage())
    }
  }, [listName, tracking.listId, geo, isSourceLpc])

  const { ref, inView } = useInView({ triggerOnce: true })
  useEffect(() => {
    if (inView && isSourceLpc) {
      Analytics.track('view-recommendation-on-lpc-page', viewRecommendationOnLpcPage())
    }
  }, [inView, isSourceLpc])

  if (offersToDisplay.length < MINIMUM_OFFERS_TO_DISPLAY || isFetching) {
    return null
  }

  return (
    <TrackingContext.Provider value={tracking}>
      <Pane>
        <PaneBody size="xl-y">
          <LayoutContainer>
            <div ref={ref}>
              <Heading variant="heading3">{title}</Heading>
            </div>
            <Spacing mt={16}>
              <TrackedCardCarousel pageSize={DISPLAY_PAGE_SIZE}>
                {offersToDisplay.map((offer, i) => (
                  <YouMayAlsoLikeTile
                    key={offer.id}
                    offer={offer}
                    position={i + 1}
                    onClick={onClick}
                    linkQuery={buildSearchStringFromFilters(filters)}
                  />
                ))}
              </TrackedCardCarousel>
            </Spacing>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </TrackingContext.Provider>
  )
}

export default memo(OfferRecommendations)
